import { useState, useEffect, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { updatePageTitle } from "../utils/titleUtils";
import { toast } from "react-toastify";
import {
  useNavigate,
  Outlet,
  NavLink,
  useLocation,
  Link,
} from "react-router-dom";
import {
  getEmployees,
  createEmployee,
} from "../api/apiFunction/employeesServices";
import { LogoWithText } from "../components/Logo";

import {
  PanelLeft,
  PanelLeftDashed,
  Sun,
  Moon,
  User,
  Users,
  LogOut,
  X,
} from "lucide-react";
import CSVUploader from "../components/pages/org/CSVUploader";
import PlanUpgradeModal from "../components/pages/org/PlanUpgradeModal";

// Skeleton loader components
const SidebarSkeleton = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between pt-[14px] px-3 pb-2">
      <div className="flex items-center">
        <div className="h-8 w-8 bg-bg-40 rounded"></div>
        <div className="ml-2 h-4 bg-bg-40 rounded w-16"></div>
      </div>
      <div className="h-6 w-6 bg-bg-40 rounded"></div>
    </div>
    <div className="px-3 py-4 space-y-1">
      {[...Array(3)].map((_, index) => (
        <div key={index} className="flex items-center px-2 py-2">
          <div className="h-4 w-4 bg-bg-40 rounded mr-3"></div>
          <div className="h-4 bg-bg-40 rounded w-20"></div>
        </div>
      ))}
    </div>
  </div>
);

const HeaderSkeleton = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between px-6 py-3 border-b border-bd-50">
      <div className="flex items-center gap-3">
        <div className="h-6 w-6 bg-bg-40 rounded"></div>
        <div className="h-6 bg-bg-40 rounded w-32"></div>
      </div>
      <div className="flex items-center gap-2">
        <div className="h-8 bg-bg-40 rounded w-16"></div>
        <div className="h-8 w-8 bg-bg-40 rounded-full"></div>
        <div className="h-8 w-8 bg-bg-40 rounded-full"></div>
      </div>
    </div>
  </div>
);

const ContentSkeleton = () => (
  <div className="animate-pulse p-6">
    <div className="max-w-6xl mx-auto">
      <div className="space-y-6">
        <div className="h-8 bg-bg-40 rounded w-48"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-bg-60 rounded-lg p-6">
              <div className="h-4 bg-bg-40 rounded w-24 mb-4"></div>
              <div className="h-8 bg-bg-40 rounded w-16 mb-2"></div>
              <div className="h-4 bg-bg-40 rounded w-32"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Organization = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showEmployeePlanModal, setShowEmployeePlanModal] = useState(false);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const location = useLocation();

  const fetchEmployees = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getEmployees(user.organization?._id);
      setEmployees(Array.isArray(response) ? response : []);
      return true;
    } catch (error) {
      toast.error("Failed to fetch employees");
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [user.organization?._id]);

  useEffect(() => {
    if (user.organization?._id) {
      fetchEmployees();
    }
    updatePageTitle("Organization");
  }, [user.organization?._id, fetchEmployees]);

  const toggleSidebar = () => {
    setSidebarExpanded(!sidebarExpanded);
  };

  // Handle bulk employee import
  const handleBulkImport = async (employees) => {
    try {
      setIsLoading(true);
      let successCount = 0;

      // Process each employee
      for (const emp of employees) {
        try {
          // Match the same payload structure as single employee addition
          await createEmployee({
            name: emp.name,
            email: emp.email,
            organizationId: user.organization?._id,
          });

          successCount++;
        } catch (error) {
          // Continue with next employee even if one fails
        }
      }

      // Refresh employee list
      await fetchEmployees();

      if (successCount > 0) {
        toast.success(`Successfully invited ${successCount} employee(s)`);
      }
      if (successCount < employees.length) {
        toast.warning(
          `Failed to add ${employees.length - successCount} employee(s)`
        );
      }

      setShowAddEmployeeModal(false);
    } catch (error) {
      toast.error("Error adding employees. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setTimeout(() => {
      window.location.href = "/login";
    }, 1500);
  };

  return (
    <div className="flex h-screen bg-bg-50 text-fg-50 overflow-hidden">
      {/* Sidebar */}
      <div
        className={`${
          sidebarExpanded ? "w-64" : "w-0"
        } border-r border-bd-50 bg-bg-60 flex flex-col transition-all duration-300 ease-in-out overflow-hidden`}
      >
        {isLoading ? (
          <SidebarSkeleton />
        ) : (
          <>
            {/* Header with Logo and Role */}
            <div className="flex items-center justify-between pt-[14px] px-3 pb-2">
              <div className="flex items-center">
                {/* Logo */}
                <div className="flex-shrink-0">
                  <Link to="/" className="flex items-center">
                    <LogoWithText
                      className="h-7 w-auto max-w-32"
                      fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
                    />
                    {user?.role && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-md text-xs font-mono font-semibold bg-bg-40 text-fg-60">
                        {user.role.replace(/_/g, " ").toUpperCase()}
                      </span>
                    )}
                  </Link>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex-grow overflow-y-auto custom-scrollbar">
              <div className="px-3 py-4 space-y-1">
                <NavLink
                  to="dashboard"
                  className={({ isActive }) =>
                    `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                      isActive
                        ? "bg-bg-40 text-fg-50"
                        : "text-fg-60 hover:bg-bg-50/20"
                    }`
                  }
                  end
                >
                  <PanelLeft size={18} className="mr-3" />
                  <span>Dashboard</span>
                </NavLink>
                <NavLink
                  to="employees"
                  className={({ isActive }) =>
                    `flex items-center w-full px-2 py-2 text-sm rounded-md ${
                      isActive
                        ? "bg-bg-40 text-fg-50"
                        : "text-fg-60 hover:bg-bg-50/20"
                    }`
                  }
                >
                  <Users size={18} className="mr-3" />
                  <span>Employees</span>
                </NavLink>
              </div>
            </div>

            {/* User Profile */}
            <div className="p-3">
              <div className="space-y-1">
                <div className="p-3 flex items-center hover:bg-bg-50 rounded-md cursor-pointer">
                  <div className="flex-shrink-0 h-8 w-8 rounded-full bg-ac-02 flex items-center justify-center text-fg-40 shadow-sm">
                    <User size={16} />
                  </div>
                  {sidebarExpanded && (
                    <div className="ml-3 overflow-hidden">
                      <p className="text-sm font-medium truncate">
                        {user.name || "Organization Admin"}
                      </p>
                      <p className="text-xs text-fg-60 truncate">
                        {user.email || "No email"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        {isLoading ? (
          <HeaderSkeleton />
        ) : (
          <div className="flex items-center justify-between px-6 py-3 border-b border-bd-50">
            <div className="flex items-center gap-3">
              <button
                onClick={toggleSidebar}
                className="p-2 text-fg-60 hover:text-fg-50 transition-colors"
              >
                {sidebarExpanded ? (
                  <PanelLeftDashed size={20} />
                ) : (
                  <PanelLeft size={20} />
                )}
              </button>
              <h1 className="text-lg font-semibold">
                {location.pathname.split("/").pop() === "employees"
                  ? "Manage Employees"
                  : location.pathname.split("/").pop() === "plans"
                  ? "Subscription Plans"
                  : "Dashboard"}
              </h1>
            </div>
            <div className="flex items-center gap-2">
              {/* Current Plan Badge */}
              {(() => {
                const user = JSON.parse(localStorage.getItem("user") || "{}");
                const planName = (user.plan?.name || "Free").toLowerCase();
                let badgeClass =
                  "bg-gray-100 text-gray-600 dark:bg-gray-900/40 dark:text-gray-200";
                let borderGradient = "from-gray-300 via-gray-400 to-gray-500";
                if (planName === "free") {
                  badgeClass =
                    "bg-yellow-100 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-200";
                  borderGradient =
                    "from-yellow-300 via-yellow-400 to-yellow-500";
                } else if (planName === "pro") {
                  badgeClass =
                    "bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-200";
                  borderGradient = "from-blue-400 via-blue-500 to-blue-600";
                } else if (planName === "enterprise") {
                  badgeClass =
                    "bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-200";
                  borderGradient =
                    "from-purple-400 via-purple-500 to-purple-600";
                }
                return (
                  <button
                    type="button"
                    onClick={() => navigate("/org/plans")}
                    className={`relative mr-2 p-[2px] rounded-full bg-gradient-to-r ${borderGradient} transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ac-02 hover:opacity-80 cursor-pointer`}
                    title="View and change plan"
                  >
                    <span
                      className={`block px-4 py-1 rounded-full text-sm font-semibold shadow-sm ${badgeClass} bg-clip-padding`.trim()}
                    >
                      {user.plan?.name
                        ? user.plan.name.charAt(0).toUpperCase() +
                          user.plan.name.slice(1)
                        : "Free"}
                    </span>
                  </button>
                );
              })()}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full text-fg-60 hover:text-fg-50 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
              </button>
              <button
                className="p-2 rounded-full text-fg-60 hover:text-fg-50 transition-colors"
                aria-label="Logout"
              >
                <LogOut onClick={handleLogout} size={18} />
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          <div className="max-w-6xl mx-auto">
            {isLoading ? (
              <ContentSkeleton />
            ) : (
              <Outlet
                key={location.pathname}
                context={{ employees, currentPlan: user.plan }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add Employee Modal */}
      {showAddEmployeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-bg-60 rounded-lg shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Add Employee(s)</h3>
              <button
                onClick={() => {
                  setShowAddEmployeeModal(false);
                }}
                className="text-fg-60 hover:text-fg-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="mt-4">
              <CSVUploader
                onFileProcessed={handleBulkImport}
                onClose={() => setShowAddEmployeeModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Plan Upgrade Modal */}
      <PlanUpgradeModal
        isOpen={showPlanModal}
        onClose={() => setShowPlanModal(false)}
        currentPlanId={user.organization?.plan}
        onPlanSelect={(plan) => {
          toast.success(`Plan updated to ${plan.name}`);
          setShowPlanModal(false);
        }}
      />

      <PlanUpgradeModal
        isOpen={showEmployeePlanModal}
        onClose={() => setShowEmployeePlanModal(false)}
        currentPlanId="free"
        onPlanSelect={(plan) => {
          toast.success(`Plan updated to ${plan.name}`);
          setShowEmployeePlanModal(false);
        }}
      />
    </div>
  );
};

export default Organization;
